<script setup lang="ts">
/**
 * The game's wordmark, and the only drawing of it.
 *
 * Two headers wear it — the landing page's lintel and the garage's title bar —
 * and they are the two headers a visitor sees either side of pressing Play, so
 * a second copy would be two marks that agree on the day they are written and
 * disagree on some later one. Same argument `KeyCap.vue` and `CurrencyIcon.vue`
 * make: one control, one drawing.
 *
 * ## It is authored art, not drawn, and that does not break the rule
 *
 * Everything else this UI puts in a bar at ~15 px is drawn in SVG or CSS — the
 * radar bezel, the title badges, the key caps, the quest card's metric marks —
 * because a fixed raster sized *to* the display is soft on half the machines
 * that play the game. The escape is the diamond's: oversample rather than
 * upsample. The file is 622x96 and it renders 24 px tall, so at DPR 3 it is
 * still being downsampled, which is the direction that rule cares about. What a
 * generated wordmark buys over a font-plus-hazard-square lockup is the
 * stencilled letterforms themselves, which no available face carries.
 *
 * The paint grain the render came with was flattened onto the product's own
 * three tokens (`--bone`, `--accent`, `--frost`) before export: at bar size the
 * grain is invisible and it was two thirds of the file, and snapping the amber
 * puts the mark on the exact accent rather than near it.
 *
 * ## A missing file degrades to the wordmark it replaced
 *
 * The rank chip falls back to the rank's name and the currency mark to its
 * drawn facets, for the reason that applies most sharply here: a header whose
 * identity silently vanishes is worse than a plainer one. The fallback is the
 * `Steel of War` lockup this component replaced, and it takes its face from
 * whatever the host header sets — the two type systems differ on purpose, so
 * the mark must not import either.
 *
 * Height comes from `--brand-logo-height` so each bar states its own; the width
 * follows the aspect and is never set, or the letters stretch.
 */
import { ref } from 'vue';

const failed = ref(false);
</script>

<template>
  <span class="brand-logo" role="img" aria-label="Steel of War">
    <img
      v-if="!failed"
      src="/assets/ui/logo.webp"
      alt=""
      decoding="async"
      @error="failed = true"
    />
    <span v-else class="fallback" aria-hidden="true">Steel<em>of</em>War</span>
  </span>
</template>

<style scoped>
.brand-logo {
  display: inline-flex;
  align-items: center;
  flex: none;
}

.brand-logo img {
  display: block;
  height: var(--brand-logo-height, 1.5rem);
  width: auto;
}

/* The face is the host header's; only the treatment is stated here, so the
   fallback reads as part of whichever bar it lands in. */
.fallback {
  font: inherit;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  line-height: 1;
  font-synthesis: none;
  white-space: nowrap;
}

.fallback em {
  font-style: normal;
  margin: 0 0.15em;
  opacity: 0.7;
}
</style>
